/**
 * Solid voxelization of a triangle mesh (auto-skin v2, plan D-v2-1).
 *
 * Pipeline: conservative surface rasterization (exact triangle/box SAT overlap) → 6-connected
 * dilation of the shell by `closeRadius` → 6-connected exterior flood fill from the grid boundary
 * → the dilation is peeled back off the exterior side → everything not reached and not surface is
 * interior. The domain the geodesic solver walks is `surface ∪ interior`, exposed as a compact
 * index.
 *
 * The dilate/erode pair straddles the flood on purpose. Eroding the shell *before* the flood (a
 * literal morphological close of the surface) is a no-op on a one-cell-thick shell: the plug that
 * dilation puts in a hole is one cell thick too, so it always has an empty neighbour perpendicular
 * to the wall and erosion always takes it back — measured, `closeRadius` 0 and 1 gave identical
 * counts on holed spheres. Dilating first lets the plug block the flood; peeling afterwards only
 * shrinks the *exterior* side, so the sealed volume stays interior and the domain is not inflated.
 *
 * Pure module: no DOM, no editor state, no three imports. Deterministic. Only two full-grid arrays
 * are allocated (`state` Uint8, `domainIndex` Int32); the flood borrows `domainIndex` as its queue.
 */

import { AppError } from '../../shared/errors';
import type { Vec3Tuple } from '../../shared/types';
import type { VoxelGrid } from './types';

const DEFAULT_PADDING = 2;
const DEFAULT_CLOSE_RADIUS = 1;
const DEFAULT_NEAREST_RADIUS = 8;

/** Build-time cell states; the finished grid only exposes 0/1/2 (see `VoxelState`). */
const EMPTY = 0;
const SURFACE = 1;
const INTERIOR = 2;
/** Added by the dilation pass — the only cells erosion is allowed to take back. */
const DILATED = 3;
/** Reached by the exterior flood. */
const VISITED = 4;
/** Scheduled for the next peel layer. */
const QUEUED = 5;

/** Relative slack on the cell half-extent so a triangle exactly touching a face still counts. */
const TOUCH_EPSILON = 1e-6;

export interface VoxelGridOptions {
  /** Cells along the longest bbox axis. */
  resolution: number;
  /** Empty cells added on every side so the exterior flood always has a way in. Default 2. */
  paddingCells?: number;
  /** Morphological close radius in cells (6-connected). Default 1. */
  closeRadius?: number;
}

// Triangle / axis-aligned box overlap (Akenine-Möller SAT, 13 axes)

function separated(p: number, q: number, rad: number): boolean {
  const min = p < q ? p : q;
  const max = p < q ? q : p;
  return min > rad || max < -rad;
}

function separated3(p0: number, p1: number, p2: number, rad: number): boolean {
  let min = p0;
  let max = p0;
  if (p1 < min) min = p1;
  else if (p1 > max) max = p1;
  if (p2 < min) min = p2;
  else if (p2 > max) max = p2;
  return min > rad || max < -rad;
}

/** Does the plane through (nx,ny,nz)·x = d intersect the box [-h,h]³ centred at the origin? */
function planeBoxOverlap(nx: number, ny: number, nz: number, d: number, h: number): boolean {
  const r = h * (Math.abs(nx) + Math.abs(ny) + Math.abs(nz));
  return Math.abs(d) <= r;
}

/**
 * Exact overlap test between the cubic cell centred at (cx,cy,cz) with half extent `h` and the
 * triangle (a,b,c). Exact overlap is 6-separating for a closed surface, which is what the
 * exterior flood below needs.
 */
function triBoxOverlap(
  cx: number, cy: number, cz: number, h: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  gx: number, gy: number, gz: number,
): boolean {
  const v0x = ax - cx, v0y = ay - cy, v0z = az - cz;
  const v1x = bx - cx, v1y = by - cy, v1z = bz - cz;
  const v2x = gx - cx, v2y = gy - cy, v2z = gz - cz;

  // 3 box face normals
  if (separated3(v0x, v1x, v2x, h)) return false;
  if (separated3(v0y, v1y, v2y, h)) return false;
  if (separated3(v0z, v1z, v2z, h)) return false;

  const e0x = v1x - v0x, e0y = v1y - v0y, e0z = v1z - v0z;
  const e1x = v2x - v1x, e1y = v2y - v1y, e1z = v2z - v1z;
  const e2x = v0x - v2x, e2y = v0y - v2y, e2z = v0z - v2z;

  // 9 edge × axis cross products (the AXISTEST_* macros of the original paper)
  const r0zy = (Math.abs(e0z) + Math.abs(e0y)) * h;
  const r0zx = (Math.abs(e0z) + Math.abs(e0x)) * h;
  const r0yx = (Math.abs(e0y) + Math.abs(e0x)) * h;
  if (separated(e0z * v0y - e0y * v0z, e0z * v2y - e0y * v2z, r0zy)) return false;
  if (separated(-e0z * v0x + e0x * v0z, -e0z * v2x + e0x * v2z, r0zx)) return false;
  if (separated(e0y * v1x - e0x * v1y, e0y * v2x - e0x * v2y, r0yx)) return false;

  const r1zy = (Math.abs(e1z) + Math.abs(e1y)) * h;
  const r1zx = (Math.abs(e1z) + Math.abs(e1x)) * h;
  const r1yx = (Math.abs(e1y) + Math.abs(e1x)) * h;
  if (separated(e1z * v0y - e1y * v0z, e1z * v2y - e1y * v2z, r1zy)) return false;
  if (separated(-e1z * v0x + e1x * v0z, -e1z * v2x + e1x * v2z, r1zx)) return false;
  if (separated(e1y * v0x - e1x * v0y, e1y * v1x - e1x * v1y, r1yx)) return false;

  const r2zy = (Math.abs(e2z) + Math.abs(e2y)) * h;
  const r2zx = (Math.abs(e2z) + Math.abs(e2x)) * h;
  const r2yx = (Math.abs(e2y) + Math.abs(e2x)) * h;
  if (separated(e2z * v0y - e2y * v0z, e2z * v1y - e2y * v1z, r2zy)) return false;
  if (separated(-e2z * v0x + e2x * v0z, -e2z * v1x + e2x * v1z, r2zx)) return false;
  if (separated(e2y * v1x - e2x * v1y, e2y * v2x - e2x * v2y, r2yx)) return false;

  // triangle plane
  const nx = e0y * e1z - e0z * e1y;
  const ny = e0z * e1x - e0x * e1z;
  const nz = e0x * e1y - e0y * e1x;
  return planeBoxOverlap(nx, ny, nz, nx * v0x + ny * v0y + nz * v0z, h);
}

// Grid construction

function clampInt(v: number, hi: number): number {
  return v < 0 ? 0 : v > hi ? hi : v;
}

export function buildVoxelGrid(
  positions: ArrayLike<number>,
  index: ArrayLike<number> | null,
  opts: VoxelGridOptions,
): VoxelGrid {
  const t0 = performance.now();

  const vertexCount = Math.floor(positions.length / 3);
  if (vertexCount < 3) {
    throw new AppError('VOXEL_NO_GEOMETRY', 'Cannot voxelize a mesh with fewer than 3 vertices.', {
      vertexCount,
    });
  }
  const resolution = Math.max(2, Math.floor(opts.resolution));
  const padding = Math.max(0, Math.floor(opts.paddingCells ?? DEFAULT_PADDING));
  const closeRadius = Math.max(0, Math.floor(opts.closeRadius ?? DEFAULT_CLOSE_RADIUS));

  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let v = 0; v < vertexCount; v++) {
    const x = positions[v * 3];
    const y = positions[v * 3 + 1];
    const z = positions[v * 3 + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
    throw new AppError('VOXEL_BAD_BOUNDS', 'The mesh positions contain no finite bounding box.');
  }

  const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  const cell = extent > 1e-9 ? extent / resolution : 1 / resolution;
  const dims: [number, number, number] = [
    Math.max(1, Math.ceil((maxX - minX) / cell)) + 2 * padding,
    Math.max(1, Math.ceil((maxY - minY) / cell)) + 2 * padding,
    Math.max(1, Math.ceil((maxZ - minZ) / cell)) + 2 * padding,
  ];
  const origin: Vec3Tuple = [minX - padding * cell, minY - padding * cell, minZ - padding * cell];

  const [dx, dy, dz] = dims;
  const strideZ = dx * dy;
  const total = strideZ * dz;
  const state = new Uint8Array(total);

  rasterizeSurface(positions, index, state, dims, origin, cell);

  if (closeRadius > 0) dilateSurface(state, dims, closeRadius);

  // `domainIndex` doubles as the BFS queue: every cell is enqueued at most once, so `total`
  // entries always suffice and no third full-grid array is needed.
  const domainIndex = new Int32Array(total);
  floodExterior(state, dims, domainIndex);

  if (closeRadius > 0) peelDilatedExterior(state, dims, closeRadius);

  let surface = 0;
  let interior = 0;
  let exterior = 0;
  for (let i = 0; i < total; i++) {
    const s = state[i];
    if (s === VISITED) {
      state[i] = EMPTY;
      exterior++;
    } else if (s === SURFACE) {
      surface++;
    } else {
      // Empty-but-unreachable, plus whatever dilation added and the peel did not take back.
      state[i] = INTERIOR;
      interior++;
    }
  }

  const domainCount = surface + interior;
  const domainCells = new Int32Array(domainCount);
  domainIndex.fill(-1);
  let compact = 0;
  for (let i = 0; i < total; i++) {
    if (state[i] !== EMPTY) {
      domainIndex[i] = compact;
      domainCells[compact] = i;
      compact++;
    }
  }

  return {
    dims,
    origin,
    cell,
    state,
    domainIndex,
    domainCells,
    domainCount,
    stats: {
      surface,
      interior,
      exterior,
      // "The flood did not eat the volume". The threshold is 0.2, not the 0.02 first tried: the
      // orc at resolution 192 without closing leaked deep into the torso (interior 87k vs 358k
      // with closing, torso bones unreachable) and still passed the 0.02 rule.
      closed: interior >= 0.2 * surface,
      buildMs: performance.now() - t0,
    },
  };
}

function rasterizeSurface(
  positions: ArrayLike<number>,
  index: ArrayLike<number> | null,
  state: Uint8Array,
  dims: [number, number, number],
  origin: Vec3Tuple,
  cell: number,
): void {
  const [dx, dy, dz] = dims;
  const strideZ = dx * dy;
  const half = 0.5 * cell * (1 + TOUCH_EPSILON);
  const triCount = index ? Math.floor(index.length / 3) : Math.floor(positions.length / 9);

  for (let t = 0; t < triCount; t++) {
    const i0 = index ? index[t * 3] * 3 : t * 9;
    const i1 = index ? index[t * 3 + 1] * 3 : t * 9 + 3;
    const i2 = index ? index[t * 3 + 2] * 3 : t * 9 + 6;

    const ax = positions[i0], ay = positions[i0 + 1], az = positions[i0 + 2];
    const bx = positions[i1], by = positions[i1 + 1], bz = positions[i1 + 2];
    const gx = positions[i2], gy = positions[i2 + 1], gz = positions[i2 + 2];

    const x0 = clampInt(Math.floor((Math.min(ax, bx, gx) - origin[0]) / cell) - 1, dx - 1);
    const x1 = clampInt(Math.floor((Math.max(ax, bx, gx) - origin[0]) / cell) + 1, dx - 1);
    const y0 = clampInt(Math.floor((Math.min(ay, by, gy) - origin[1]) / cell) - 1, dy - 1);
    const y1 = clampInt(Math.floor((Math.max(ay, by, gy) - origin[1]) / cell) + 1, dy - 1);
    const z0 = clampInt(Math.floor((Math.min(az, bz, gz) - origin[2]) / cell) - 1, dz - 1);
    const z1 = clampInt(Math.floor((Math.max(az, bz, gz) - origin[2]) / cell) + 1, dz - 1);

    for (let z = z0; z <= z1; z++) {
      const cz = origin[2] + (z + 0.5) * cell;
      for (let y = y0; y <= y1; y++) {
        const cy = origin[1] + (y + 0.5) * cell;
        const rowBase = z * strideZ + y * dx;
        for (let x = x0; x <= x1; x++) {
          const idx = rowBase + x;
          if (state[idx] === SURFACE) continue;
          const cx = origin[0] + (x + 0.5) * cell;
          if (triBoxOverlap(cx, cy, cz, half, ax, ay, az, bx, by, bz, gx, gy, gz)) {
            state[idx] = SURFACE;
          }
        }
      }
    }
  }
}

/**
 * 6-connected dilation of the shell by `radius`. Added cells are tagged `DILATED` so the peel
 * afterwards can only take back what dilation added — an original surface cell is never lost.
 */
function dilateSurface(state: Uint8Array, dims: [number, number, number], radius: number): void {
  const [dx, dy, dz] = dims;
  const strideZ = dx * dy;
  const total = strideZ * dz;

  let frontier: number[] = [];
  for (let i = 0; i < total; i++) if (state[i] === SURFACE) frontier.push(i);

  for (let r = 0; r < radius; r++) {
    const next: number[] = [];
    for (let k = 0; k < frontier.length; k++) {
      const idx = frontier[k];
      const x = idx % dx;
      const y = ((idx / dx) | 0) % dy;
      const z = (idx / strideZ) | 0;
      // The outermost cell layer is never dilated into: the exterior flood starts there and must
      // stay unobstructed (a grid-aligned mesh already rasterizes one cell into the padding).
      if (x > 1 && state[idx - 1] === EMPTY) { state[idx - 1] = DILATED; next.push(idx - 1); }
      if (x < dx - 2 && state[idx + 1] === EMPTY) { state[idx + 1] = DILATED; next.push(idx + 1); }
      if (y > 1 && state[idx - dx] === EMPTY) { state[idx - dx] = DILATED; next.push(idx - dx); }
      if (y < dy - 2 && state[idx + dx] === EMPTY) { state[idx + dx] = DILATED; next.push(idx + dx); }
      if (z > 1 && state[idx - strideZ] === EMPTY) { state[idx - strideZ] = DILATED; next.push(idx - strideZ); }
      if (z < dz - 2 && state[idx + strideZ] === EMPTY) { state[idx + strideZ] = DILATED; next.push(idx + strideZ); }
    }
    frontier = next;
    if (next.length === 0) break;
  }
}

/**
 * Undo the dilation on the exterior side only: `radius` layers of `DILATED` cells touching the
 * flooded exterior become exterior themselves. Interior cells are never converted, so a hole the
 * dilation plugged stays plugged — only the outward inflation of the shell is removed.
 */
function peelDilatedExterior(state: Uint8Array, dims: [number, number, number], radius: number): void {
  const [dx, dy, dz] = dims;
  const strideZ = dx * dy;

  let frontier: number[] = [];
  let idx = 0;
  for (let z = 0; z < dz; z++) {
    for (let y = 0; y < dy; y++) {
      for (let x = 0; x < dx; x++, idx++) {
        if (state[idx] !== DILATED) continue;
        const touchesExterior =
          (x > 0 && state[idx - 1] === VISITED) ||
          (x < dx - 1 && state[idx + 1] === VISITED) ||
          (y > 0 && state[idx - dx] === VISITED) ||
          (y < dy - 1 && state[idx + dx] === VISITED) ||
          (z > 0 && state[idx - strideZ] === VISITED) ||
          (z < dz - 1 && state[idx + strideZ] === VISITED);
        if (touchesExterior) {
          state[idx] = QUEUED;
          frontier.push(idx);
        }
      }
    }
  }

  for (let r = 0; r < radius && frontier.length > 0; r++) {
    for (let k = 0; k < frontier.length; k++) state[frontier[k]] = VISITED;
    if (r + 1 === radius) break;

    const next: number[] = [];
    for (let k = 0; k < frontier.length; k++) {
      const c = frontier[k];
      const x = c % dx;
      const y = ((c / dx) | 0) % dy;
      const z = (c / strideZ) | 0;
      if (x > 0 && state[c - 1] === DILATED) { state[c - 1] = QUEUED; next.push(c - 1); }
      if (x < dx - 1 && state[c + 1] === DILATED) { state[c + 1] = QUEUED; next.push(c + 1); }
      if (y > 0 && state[c - dx] === DILATED) { state[c - dx] = QUEUED; next.push(c - dx); }
      if (y < dy - 1 && state[c + dx] === DILATED) { state[c + dx] = QUEUED; next.push(c + dx); }
      if (z > 0 && state[c - strideZ] === DILATED) { state[c - strideZ] = QUEUED; next.push(c - strideZ); }
      if (z < dz - 1 && state[c + strideZ] === DILATED) { state[c + strideZ] = QUEUED; next.push(c + strideZ); }
    }
    frontier = next;
  }
}

/** 6-connected BFS from every empty grid-boundary cell; `queue` is scratch of length `total`. */
function floodExterior(state: Uint8Array, dims: [number, number, number], queue: Int32Array): void {
  const [dx, dy, dz] = dims;
  const strideZ = dx * dy;
  let tail = 0;

  let idx = 0;
  for (let z = 0; z < dz; z++) {
    for (let y = 0; y < dy; y++) {
      for (let x = 0; x < dx; x++, idx++) {
        const onBoundary = x === 0 || x === dx - 1 || y === 0 || y === dy - 1 || z === 0 || z === dz - 1;
        if (onBoundary && state[idx] === EMPTY) {
          state[idx] = VISITED;
          queue[tail++] = idx;
        }
      }
    }
  }

  for (let head = 0; head < tail; head++) {
    const c = queue[head];
    const x = c % dx;
    const y = ((c / dx) | 0) % dy;
    const z = (c / strideZ) | 0;
    if (x > 0 && state[c - 1] === EMPTY) { state[c - 1] = VISITED; queue[tail++] = c - 1; }
    if (x < dx - 1 && state[c + 1] === EMPTY) { state[c + 1] = VISITED; queue[tail++] = c + 1; }
    if (y > 0 && state[c - dx] === EMPTY) { state[c - dx] = VISITED; queue[tail++] = c - dx; }
    if (y < dy - 1 && state[c + dx] === EMPTY) { state[c + dx] = VISITED; queue[tail++] = c + dx; }
    if (z > 0 && state[c - strideZ] === EMPTY) { state[c - strideZ] = VISITED; queue[tail++] = c - strideZ; }
    if (z < dz - 1 && state[c + strideZ] === EMPTY) { state[c + strideZ] = VISITED; queue[tail++] = c + strideZ; }
  }
}

// Lookups

/** Full grid index of the cell containing `p`, clamped into the grid. */
export function cellOfPoint(grid: VoxelGrid, p: Vec3Tuple): number {
  const [dx, dy, dz] = grid.dims;
  const x = clampInt(Math.floor((p[0] - grid.origin[0]) / grid.cell), dx - 1);
  const y = clampInt(Math.floor((p[1] - grid.origin[1]) / grid.cell), dy - 1);
  const z = clampInt(Math.floor((p[2] - grid.origin[2]) / grid.cell), dz - 1);
  return x + dx * (y + dy * z);
}

/** Centre of the cell in model space. */
export function cellCenter(grid: VoxelGrid, fullIdx: number): Vec3Tuple {
  const [dx, dy] = grid.dims;
  const x = fullIdx % dx;
  const y = ((fullIdx / dx) | 0) % dy;
  const z = (fullIdx / (dx * dy)) | 0;
  return [
    grid.origin[0] + (x + 0.5) * grid.cell,
    grid.origin[1] + (y + 0.5) * grid.cell,
    grid.origin[2] + (z + 0.5) * grid.cell,
  ];
}

/**
 * Compact index of the domain cell nearest to `p`, searching outward in Chebyshev shells.
 * Returns -1 when no domain cell is within `maxRadiusCells`.
 */
export function nearestDomainCell(
  grid: VoxelGrid,
  p: Vec3Tuple,
  maxRadiusCells: number = DEFAULT_NEAREST_RADIUS,
): number {
  const [dx, dy, dz] = grid.dims;
  const start = cellOfPoint(grid, p);
  if (grid.domainIndex[start] >= 0) return grid.domainIndex[start];

  const sx = start % dx;
  const sy = ((start / dx) | 0) % dy;
  const sz = (start / (dx * dy)) | 0;
  const maxR = Math.max(0, Math.floor(maxRadiusCells));

  for (let r = 1; r <= maxR; r++) {
    let best = -1;
    let bestD = Infinity;
    for (let z = Math.max(0, sz - r); z <= Math.min(dz - 1, sz + r); z++) {
      for (let y = Math.max(0, sy - r); y <= Math.min(dy - 1, sy + r); y++) {
        const onShellYZ = Math.abs(y - sy) === r || Math.abs(z - sz) === r;
        for (let x = Math.max(0, sx - r); x <= Math.min(dx - 1, sx + r); x++) {
          // Only the shell of Chebyshev radius r; inner radii were checked already.
          if (!onShellYZ && Math.abs(x - sx) !== r) continue;
          const compact = grid.domainIndex[x + dx * (y + dy * z)];
          if (compact < 0) continue;
          const cx = grid.origin[0] + (x + 0.5) * grid.cell - p[0];
          const cy = grid.origin[1] + (y + 0.5) * grid.cell - p[1];
          const cz = grid.origin[2] + (z + 0.5) * grid.cell - p[2];
          const d = cx * cx + cy * cy + cz * cz;
          if (d < bestD) { bestD = d; best = compact; }
        }
      }
    }
    if (best >= 0) return best;
  }
  return -1;
}
